import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';

interface RedshiftTablePickerProps {
  /** Chosen canonical table names. Empty means every table in the schema. */
  value: string[];
  onChange: (next: string[]) => void;
  options: readonly string[];
  /** True when the run's exclusions would drop this table despite being picked. */
  isExcluded?: (table: string) => boolean;
}

/**
 * Multi-select over the canonical table names. An empty selection is the
 * meaningful default rather than an unset field — the pipeline only applies an
 * include filter when the list is non-empty, so selecting nothing exports the
 * whole schema.
 */
export function RedshiftTablePicker({
  value,
  onChange,
  options,
  isExcluded,
}: RedshiftTablePickerProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const selected = useMemo(() => new Set(value), [value]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((table) => table.includes(q)) : [...options];
  }, [options, query]);

  // The list shrinks as the query narrows, which can leave the highlight past
  // its end.
  const active = Math.min(highlight, Math.max(matches.length - 1, 0));

  function toggle(table: string) {
    onChange(selected.has(table) ? value.filter((t) => t !== table) : [...value, table]);
    setQuery('');
    inputRef.current?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(Math.min(active + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(Math.max(active - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = matches[active];
      if (pick) toggle(pick);
    } else if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'Backspace' && !query && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  }

  return (
    <div className="relative" ref={containerRef}>
      <div
        onClick={() => {
          setOpen(true);
          inputRef.current?.focus();
        }}
        className={`flex flex-wrap items-center gap-1.5 px-2 py-1.5 border rounded bg-white cursor-text ${
          open ? 'border-[#2383e2] ring-1 ring-[#2383e2]' : 'border-[#e9e9e7]'
        }`}
      >
        {value.map((table) => (
          <span
            key={table}
            className={`inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded text-xs font-mono ${
              isExcluded?.(table)
                ? 'bg-[#fbe8e8] text-[#e03e3e]'
                : 'bg-[#f1f1ef] text-[#37352f]'
            }`}
          >
            {table}
            <button
              type="button"
              aria-label={`Remove ${table}`}
              onClick={(e) => {
                e.stopPropagation();
                onChange(value.filter((t) => t !== table));
              }}
              className="text-[#aeaaa4] hover:text-[#e03e3e]"
            >
              <X size={11} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          className="flex-1 min-w-[8rem] text-sm text-[#37352f] placeholder:text-[#aeaaa4] focus:outline-none bg-transparent font-mono"
          placeholder={value.length === 0 ? 'All tables — pick to narrow…' : 'Add another…'}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setHighlight(0);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        <ChevronDown size={14} className="text-[#aeaaa4] shrink-0" />
      </div>

      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto rounded border border-[#e9e9e7] bg-white shadow-lg">
          {matches.length === 0 ? (
            <div className="px-3 py-2 text-xs text-[#aeaaa4]">
              No standard table matches “{query.trim()}”. Add it as a pattern under
              the advanced options.
            </div>
          ) : (
            matches.map((table, i) => {
              const isOn = selected.has(table);
              return (
                <button
                  type="button"
                  key={table}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => toggle(table)}
                  className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm font-mono ${
                    i === active ? 'bg-[#f1f1ef]' : 'bg-white'
                  } ${isOn ? 'text-[#0f7b6c]' : 'text-[#37352f]'}`}
                >
                  <span className="truncate">{table}</span>
                  <span className="flex items-center gap-2 shrink-0">
                    {isExcluded?.(table) && (
                      <span className="text-[10px] text-[#e03e3e]">excluded</span>
                    )}
                    {isOn && <Check size={13} />}
                  </span>
                </button>
              );
            })
          )}
          <div className="flex items-center justify-between gap-2 px-3 py-1.5 text-[10px] text-[#aeaaa4] border-t border-[#f1f1ef] sticky bottom-0 bg-white">
            <span>
              {value.length === 0
                ? `${matches.length} of ${options.length} standard tables`
                : `${value.length} selected`}
            </span>
            {value.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-[#2383e2] hover:underline"
              >
                Clear (export all)
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
